import React, {
  useMemo,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  Row,
} from 'antd';
import {
  CloudSyncOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import WorkflowHandle from './WorkflowHandle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';

import useToggle from '../../common/hooks/useToggle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { updateData } from './workflowHelpers';

import {
  getEclipseWorkflowSyncFields,
  CUSTOM_MAPPINGS_KEY,
  CUSTOM_MAPPING_FIELD_TYPES,
} from '../../settings/Integrations/Eclipse/eclipse.constants';
import EclipseCustomMappingModal from '../../settings/Integrations/Eclipse/EclipseCustomMappingModal';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import colors from '../../constants/Colors';

const DISABLED_TEXT = 'This step requires a linked Eclipse division and an equipment table or material table';
const REQUIRED_FIELDS = new Set(['timecard_date']);
const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Eclipse User Queue</b> and <b>Date</b> field to be configured
    </div>
  ),
};

// eslint-disable-next-line react/function-component-definition
const textRender = (key) => ({
  fieldMappings,
  defaultValue,
  onFieldMappingsChange
}) => (
  <OnTraccrTextInput
    defaultValue={defaultValue}
    allowClear
    onChange={(e) => {
      const newMaps = {
        ...fieldMappings,
      };
      const value = e?.target?.value;

      if (value) {
        newMaps[key] = value;
      } else {
        delete newMaps[key];
      }
      onFieldMappingsChange(newMaps)
    }}
  />
);

const fieldRenderMap = {
  materialUsageTransactionType: textRender('materialUsageTransactionType'),
  materialUsageDocumentType: textRender('materialUsageDocumentType'),
  materialUsageDistributionType: textRender('materialUsageDistributionType'),
  materialTransferTransactionType: textRender('materialTransferTransactionType'),
  materialTransferDocumentType: textRender('materialTransferDocumentType'),
  materialTransferDistributionType: textRender('materialTransferDistributionType'),
};

export default ({
  setDataMap,
  setElements,
  isDisplay,
  name,
  sections = [],
} = {}) => {
  const { t } = useTranslation();
  const WorkflowEclipseSync = function WorkflowEclipseSync({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const {
      fieldMappings: initialFieldMappings = {},
      queueUser: initialQueueUser,
    } = data;

    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [queueUser, setQueueUser] = useState(initialQueueUser);

    const onFieldMappingsChange = useCallback((newMappings) => {
      if (!setDataMap || !id) return;
      setFieldMappings(newMappings);
      setDataMap(updateData(id, { fieldMappings: newMappings }));
    }, [setDataMap, id]);

    const onQueueUserChange = useCallback((e) => {
      if (!setDataMap || !id) return;
      const newUser = e?.target?.value;
      setQueueUser(newUser);
      setDataMap(updateData(id, { queueUser: newUser }));
    }, [setDataMap, id]);

    const style = useMemo(() => {
      if (disabled) return { opacity: 0.7 };
      return draggable ? {} : { width: 220 };
    }, [disabled, draggable]);

    const hover = useMemo(() => (
      disabled
        ? DISABLED_TEXT
        : 'Use this step to sync with Eclipse'
    ), [disabled]);

    const eclipseFields = useMemo(() => getEclipseWorkflowSyncFields(t), [t]);
    const customMappings = fieldMappings?.[CUSTOM_MAPPINGS_KEY] ?? [];

    const mappingFields = useMemo(() => (
      eclipseFields.concat(customMappings.map((field) => ({
        id: field.value,
        key: field.value,
        text: field.label,
        types: CUSTOM_MAPPING_FIELD_TYPES,
        isCustom: true,
      })))
    ), [eclipseFields, customMappings]);

    const {
      isToggled: isMappingDrawerVisible,
      toggle: toggleMappingDrawer,
    } = useToggle();

    const [customMappingModalVisible, setCustomMappingModalVisible] = useState(false);
    const [editingMapping, setEditingMapping] = useState();

    const closeCustomMappingModal = useCallback(() => {
      setCustomMappingModalVisible(false);
      setEditingMapping();
    }, []);

    const openAddCustomMapping = useCallback(() => {
      setEditingMapping();
      setCustomMappingModalVisible(true);
    }, []);

    const openEditCustomMapping = useCallback((record) => {
      setEditingMapping({
        label: record.text,
        value: record.key,
      });
      setCustomMappingModalVisible(true);
    }, []);

    const onSaveCustomMapping = useCallback(({ label, value }) => {
      const newMaps = {
        ...fieldMappings,
      };
      if (editingMapping) {
        const { value: oldValue } = editingMapping;
        if (oldValue !== value) {
          if (newMaps[oldValue] !== undefined) {
            newMaps[value] = newMaps[oldValue];
          }
          delete newMaps[oldValue];
        }
        newMaps[CUSTOM_MAPPINGS_KEY] = customMappings.map((field) => (
          field.value === oldValue
            ? { label, value }
            : field
        ));
      } else {
        newMaps[CUSTOM_MAPPINGS_KEY] = customMappings.concat([{ label, value }]);
      }
      onFieldMappingsChange(newMaps);
      closeCustomMappingModal();
    }, [
      fieldMappings,
      customMappings,
      editingMapping,
      onFieldMappingsChange,
      closeCustomMappingModal,
    ]);

    const onDeleteCustomMapping = useCallback((fieldValue) => {
      const newMaps = {
        ...fieldMappings,
      };
      delete newMaps[fieldValue];
      newMaps[CUSTOM_MAPPINGS_KEY] = customMappings.filter(
        (field) => field.value !== fieldValue,
      );
      onFieldMappingsChange(newMaps);
    }, [fieldMappings, customMappings, onFieldMappingsChange]);

    const renderTargetField = useCallback((record) => {
      const className = REQUIRED_FIELDS.has(record.key) ? 'form-required-field' : null;
      return (
        <span className={className}>
          {record.text}
          {
            record.isCustom
            && (
              <>
                <BorderlessButton
                  iconNode={<EditOutlined style={{ marginLeft: 0 }} />}
                  style={{ width: 20 }}
                  onClick={() => openEditCustomMapping(record)}
                />
                <BorderlessButton
                  iconNode={(
                    <DeleteOutlined
                      style={{ color: colors.ONTRACCR_RED, marginLeft: 0 }}
                    />
                  )}
                  style={{ width: 20 }}
                  onClick={() => onDeleteCustomMapping(record.key)}
                />
              </>
            )
          }
        </span>
      );
    }, [openEditCustomMapping, onDeleteCustomMapping]);

    const mappingHeaderAction = (
      <OnTraccrButton
        title="Add Custom Mapping"
        icon={<PlusOutlined />}
        iconLeft
        onClick={openAddCustomMapping}
        style={{ float: 'right', marginRight: 10, marginBottom: 10 }}
      />
    );

    return (
      <WorkflowActionNode
        id={id}
        title="Sync with Eclipse"
        Icon={CloudSyncOutlined}
        type="eclipse"
        draggable={draggable}
        onNodeUpdate={setElements}
        isDisplay={isDisplay || disabled}
        hover={hover}
        style={style}
        errorPopover={
          (!queueUser || !fieldMappings?.timecard_date)
            ? ERROR_DETAILS
            : null
        }
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 5 }} className="form-required-field">
              Eclipse User Queue
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <OnTraccrTextInput
                value={queueUser}
                onChange={onQueueUserChange}
              />
            </Row>
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={toggleMappingDrawer}
              />
            </Row>
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={isMappingDrawerVisible}
              onClose={toggleMappingDrawer}
              onSubmit={toggleMappingDrawer}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onFieldMappingsChange}
              data={data}
              setDataMap={setDataMap}
              fields={mappingFields}
              requiredFields={REQUIRED_FIELDS}
              fieldRenderMap={fieldRenderMap}
              mapDropdown
              renderTargetField={renderTargetField}
              headerAction={mappingHeaderAction}
            />
            <EclipseCustomMappingModal
              visible={customMappingModalVisible}
              onClose={closeCustomMappingModal}
              onSave={onSaveCustomMapping}
              existingMappings={customMappings}
              initialMapping={editingMapping}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
  WorkflowEclipseSync.propTypes = {
    id: PropTypes.string.isRequired,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    data: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  };

  WorkflowEclipseSync.defaultProps = {
    draggable: false,
    disabled: false,
    data: {},
  };
  return WorkflowEclipseSync;
};
